import React, { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';

import Plus from '@ast/magma/components/icon/icons/Plus';

import { findFocusableElement, moveNextChildGenerator } from '@ast/magma/utils/focus';

import {
  ActionGroup,
  ActionGroupItem,
} from '@app/common/components/ActionGroup';
import { FormProviderWrapper } from '@app/common/components/FormProviderWrapper/FormProviderWrapper';
import {
  FieldInfoGroupFieldFragment as FieldInfoGroupField,
} from '@app/common/configurable-wizards/queries/queryTyping/all-fields';
import {
  FieldComponent,
  FieldLocator,
} from '@app/common/configurable-wizards/types';
import { combineNames, isFieldInfoGroupField } from '@app/common/configurable-wizards/utils';

import { useContactPermissions } from '@app/widgets/contacts-and-settings/hooks/useContactPermissions';
import { useDetailsSectionNavigator } from '@app/widgets/contacts-and-settings/hooks/useDetailsSectionNavigator';
import { useUserSettingsLocator } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsLocator';
import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';

import { SubmitModal } from '../../components/SubmitModal';
import { useSettingFieldsContext } from '../../contexts/SettingFieldsContext';
import { EmptyFields } from '../../types';
import { arrangeByPrimary } from '../../utils/arrangeByPrimary';
import { setFieldValues } from '../../utils/fieldValues';

import { DisplayEmailFieldLocator } from './EmailField';
import styles from './EmailFieldList.pcss';
import { DisplayEmailFieldInfoGroupItemLocator } from './EmailFieldListItem';

export const EmailFieldList: FieldComponent<FieldInfoGroupField> = ({ parent, field }) => {
  const formWrap = useRef<HTMLDivElement>(null);
  const { editAllowed } = useContactPermissions(field);

  // FormProviderWrapper component moves focus to the first element.
  // The solution is to remove focus from the ActionItem element.
  useEffect(() => {
    if (formWrap?.current) {
      const nextChild = moveNextChildGenerator(formWrap?.current, false);
      const element = findFocusableElement(nextChild, true);
      element?.blur();
    }
  }, []);

  const { t } = useUserSettingsTranslation();
  const methods = useForm();
  const { navigate } = useDetailsSectionNavigator();
  const { emptyFields } = useSettingFieldsContext();

  const locator = useUserSettingsLocator([
    DisplayEmailFieldInfoGroupItemLocator,
    DisplayEmailFieldLocator,
  ]);

  const emptyFieldGroup = useMemo(() => {
    const data = emptyFields?.[EmptyFields.EMAIL] as FieldInfoGroupField;
    const primary = !field.subFields.length;

    return data && setFieldValues(data, {
      IsPrimary: String(primary),
      Enabled: 'true',
    });
  }, [field, emptyFields]);

  const subFields = useMemo(() => (arrangeByPrimary(field)), [field]);

  if (subFields.length === 0 && !editAllowed) {
    return null;
  }

  return (
    <div className={styles.layout}>
      <FormProviderWrapper
        formProviderProps={methods}
        formProps={{
          onSubmit: () => { },
        }}
      >
        <div ref={formWrap}>
          <ActionGroup autoFocus={false}>
            {subFields
              .map((item, index) => {
                const subField = item as FieldInfoGroupField;
                const Component = locator(subField);

                return (
                  <ActionGroupItem
                    key={subField.id || index}
                    className={styles.groupItem}
                    iconClassName={styles.groupItemIcon}
                    onSelect={() => navigate(subField)}
                  >
                    <Component
                      parent={combineNames(parent, field.id)}
                      field={subField}
                      locator={locator}
                      key={subField.id}
                    />
                  </ActionGroupItem>
                );
              })}
          </ActionGroup>
        </div>
      </FormProviderWrapper>
      {emptyFieldGroup && editAllowed && (
        <SubmitModal
          field={emptyFieldGroup}
          successMessage={t(
            'email-fields-list.alert.success|Message text for succesful submit new email address',
            'Your email address has been successfully added',
          )}
          actionButton={(
            <ActionGroupItem
              className={styles.submitEmail}
              iconClassName={styles.hidden}
              data-stable-name="AddEmailAddressButton"
            >
              <Plus className={styles.submitEmailIcon} />
              {t(
                'email-fields-list.submit-email.action-button|Submit new email address action button',
                'Add email address',
              )}
            </ActionGroupItem>
          )}
          dialogOptions={{
            title: t(
              'email-fields-list.submit-dialog.title|Submit new email address dialog title',
              'Add new email address',
            ),
            showClose: {
              // eslint-disable-next-line i18next/no-literal-string
              'aria-label': t(
                'email-fields-list.submit-dialog.close-button.aria-label|Accessibility text for close button of submit email address dialog',
                'Close new email dialog',
              ),
            },
          }}
        />
      )}
    </div>
  );
};

export const DisplayEmailFieldInfoGroupLocator: FieldLocator = ({ field }) => (
  isFieldInfoGroupField(field) && field.id === 'EmailList'
    ? EmailFieldList
    : undefined
);
